import { useState, useMemo, useEffect, useCallback } from "react";
import "./PianificazioneTecnico.css";
import { database, timestamp } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import { ResTecnicoAccess } from "../../components/RoleBasedAccess";
import { useCollection } from "../../hooks/useCollection";
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
} from "date-fns";
import Select from "react-select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { it as itLocale } from "date-fns/locale";

// Import styles from Dashboard to reuse table styles
import "../dashboard/Dashboard.css";

const AUTO_PRESTITI_COLLECTION = "automezzi_prestiti";
const MIN_ACTIVITY_SEARCH_LENGTH = 3;

const toDateTs = (d) => {
  const dateStr = format(d, "yyyy-MM-dd");
  return timestamp.fromDate(new Date(`${dateStr}T00:00:00`));
};

const getDateStr = (d) => format(d, "yyyy-MM-dd");

const isSameDateTs = (ts, dateStr) => {
  try {
    return format(ts.toDate(), "yyyy-MM-dd") === dateStr;
  } catch {
    return false;
  }
};

const normalizeSearchText = (value) =>
  String(value || "")
    .toLocaleLowerCase("it")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const TASK_STATUS_OPEN = "open";
const TASK_STATUS_DONE = "done";

const createTaskObject = (descrizione = "", status = TASK_STATUS_OPEN) => ({
  descrizione: String(descrizione || "").trim(),
  status: status === TASK_STATUS_DONE ? TASK_STATUS_DONE : TASK_STATUS_OPEN,
});

const normalizeActivityTasks = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((task) => {
        if (typeof task === "string") {
          return createTaskObject(task, TASK_STATUS_OPEN);
        }
        if (task && typeof task === "object") {
          const descrizione = String(
            task.descrizione || task.label || task.title || task.text || "",
          ).trim();
          if (!descrizione) return null;
          return createTaskObject(descrizione, task.status);
        }
        return null;
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((task) => createTaskObject(task, TASK_STATUS_OPEN))
      .filter((task) => task.descrizione);
  }

  return [];
};

const formatTasksInline = (tasks) =>
  normalizeActivityTasks(tasks)
    .map((task) => task.descrizione)
    .filter(Boolean)
    .join(" • ");

const SELECT_MENU_PORTAL_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const getUserDisplayName = (userDoc) =>
  userDoc?.anagrafica
    ? `${userDoc.anagrafica.nome} ${userDoc.anagrafica.cognome}`
    : userDoc?.displayName || userDoc?.id || "—";

const getProjectDisplayLabel = (project) =>
  project?.nome || project?.codice || project?.id || "—";

const getVehicleDisplayLabel = (mezzo) =>
  [mezzo?.modello, mezzo?.targa].filter(Boolean).join(" ") ||
  mezzo?.targa ||
  mezzo?.modello ||
  "—";

const isAssignmentAbsence = (assignment) =>
  assignment?.status === "ok" || assignment?.status === "assenza";

const getAssignmentStatusLabel = (assignment) => {
  if (!assignment) return "Da assegnare";
  return isAssignmentAbsence(assignment) ? "Assenza" : "Assegnato";
};

const getAssignmentStatusClassName = (assignment) => {
  if (!assignment) return "unassigned";
  return isAssignmentAbsence(assignment) ? "assenza" : "assigned";
};

const getAssignmentNoteLabel = (assignment) => {
  const taskLabel = formatTasksInline(assignment?.tasks);
  return (
    [assignment?.note, taskLabel ? `Task: ${taskLabel}` : ""]
      .filter(Boolean)
      .join(" • ") || "—"
  );
};

const sanitizeOptionalText = (value) => {
  const nextValue = String(value || "").trim();
  return nextValue || null;
};

function useTaskPlannerState() {
  const [tasks, setTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState("");

  const normalizedTasks = useMemo(() => normalizeActivityTasks(tasks), [tasks]);

  const completedTasksCount = useMemo(
    () =>
      normalizedTasks.filter((task) => task.status === TASK_STATUS_DONE).length,
    [normalizedTasks],
  );

  const addTask = useCallback(
    (nextValue) => {
      const taskLabel = String(nextValue ?? taskDraft).trim();
      if (!taskLabel) return;
      setTasks((prev) => [
        ...prev,
        createTaskObject(taskLabel, TASK_STATUS_OPEN),
      ]);
      setTaskDraft("");
    },
    [taskDraft],
  );

  const updateTask = useCallback((index, value) => {
    setTasks((prev) =>
      prev.map((task, currentIndex) =>
        currentIndex === index
          ? createTaskObject(value, task?.status)
          : createTaskObject(task?.descrizione, task?.status),
      ),
    );
  }, []);

  const toggleTaskStatus = useCallback((index) => {
    setTasks((prev) =>
      prev.map((task, currentIndex) => {
        if (currentIndex !== index) {
          return createTaskObject(task?.descrizione, task?.status);
        }

        return createTaskObject(
          task?.descrizione,
          task?.status === TASK_STATUS_DONE
            ? TASK_STATUS_OPEN
            : TASK_STATUS_DONE,
        );
      }),
    );
  }, []);

  const removeTask = useCallback((index) => {
    setTasks((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== index),
    );
  }, []);

  const resetTaskPlanner = useCallback((nextTasks = []) => {
    setTasks(normalizeActivityTasks(nextTasks));
    setTaskDraft("");
  }, []);

  return {
    tasks,
    taskDraft,
    setTaskDraft,
    normalizedTasks,
    completedTasksCount,
    addTask,
    updateTask,
    toggleTaskStatus,
    removeTask,
    resetTaskPlanner,
  };
}

function SummaryCards({ cards }) {
  return (
    <div className="plan-summary-grid">
      {cards.map((card) => (
        <div key={card.label} className="plan-summary-card">
          <span>{card.label}</span>
          <strong>{card.value}</strong>
        </div>
      ))}
    </div>
  );
}

function TaskPlannerSection({
  isDisabled = false,
  tasks,
  taskDraft,
  setTaskDraft,
  addTask,
  updateTask,
  toggleTaskStatus,
  removeTask,
  completedTasksCount,
  normalizedTasks,
}) {
  return (
    <div
      className={`modal-section-card ${isDisabled ? "section-card-disabled" : ""}`}
    >
      <div className="plan-section-head">
        <div>
          <h3>Task operativi</h3>
          <p>
            Suddividi l&apos;intervento in task tracciabili e verifica il
            completamento direttamente dal modal.
          </p>
        </div>
        <div className="task-counter">
          {completedTasksCount}/{normalizedTasks.length}
        </div>
      </div>

      <div className="task-input-row">
        <input
          type="text"
          value={taskDraft}
          onChange={(e) => setTaskDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTask();
            }
          }}
          placeholder="Es. Verifica quadro, sopralluogo, report finale"
          disabled={isDisabled}
        />
        <button
          type="button"
          className="task-add-btn"
          onClick={() => addTask()}
          disabled={isDisabled || !taskDraft.trim()}
        >
          Aggiungi task
        </button>
      </div>

      {tasks.length > 0 ? (
        <div className="task-list">
          {tasks.map((task, index) => (
            <div key={`task-${index}`} className="task-item-row">
              <span className="task-item-index">{index + 1}</span>
              <label className="task-status-toggle">
                <input
                  type="checkbox"
                  checked={task?.status === TASK_STATUS_DONE}
                  onChange={() => toggleTaskStatus(index)}
                  disabled={isDisabled}
                />
                <span>
                  {task?.status === TASK_STATUS_DONE ? "Completato" : "Aperto"}
                </span>
              </label>
              <input
                type="text"
                value={task?.descrizione || ""}
                onChange={(e) => updateTask(index, e.target.value)}
                disabled={isDisabled}
                className={
                  task?.status === TASK_STATUS_DONE ? "task-input-done" : ""
                }
              />
              <button
                type="button"
                className="task-remove-btn"
                onClick={() => removeTask(index)}
                disabled={isDisabled}
              >
                Rimuovi
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="task-empty-state">
          Nessun task inserito. Puoi usare le note per il contesto e i task per
          i passaggi operativi.
        </div>
      )}
    </div>
  );
}

export default function PianificazioneTecnico() {
  const { user } = useAuthContext();
  const { documents: users } = useCollection("users", null, null, null, false);
  const { documents: projects } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: automezzi } = useCollection(
    "automezzi",
    null,
    null,
    null,
    false,
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedAutoId, setSelectedAutoId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [autoWarning, setAutoWarning] = useState("");
  const [autoPrestiti, setAutoPrestiti] = useState([]);
  const [prestitiLoading, setPrestitiLoading] = useState(false);
  const [prestitiError, setPrestitiError] = useState("");
  const [isRequestingPrestito, setIsRequestingPrestito] = useState(false);
  const [prestitoNote, setPrestitoNote] = useState("");

  const debugAutoSelect = useMemo(() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugPianificazioneAuto") === "1"
      );
    } catch {
      return false;
    }
  }, []);

  // View state
  const [viewMode, setViewMode] = useState("daily"); // "daily" | "weekly"
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [sortConfig, setSortConfig] = useState({
    key: "dipendente",
    dir: "asc",
  }); // Add sort config

  // Data state
  const [dashAssigned, setDashAssigned] = useState(new Map());
  const [weeklyAssigned, setWeeklyAssigned] = useState(new Map()); // Map<userId, Map<dateStr, assignment>>
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  // Modal state
  const [planOpen, setPlanOpen] = useState(false);
  const [isAssenza, setIsAssenza] = useState(false);
  const [planEditMode, setPlanEditMode] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [bulkSelectedProject, setBulkSelectedProject] = useState(null);
  const [bulkSelectedAutoId, setBulkSelectedAutoId] = useState(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkStartDate, setBulkStartDate] = useState(null);
  const [bulkEndDate, setBulkEndDate] = useState(null);
  const [prestitoOpen, setPrestitoOpen] = useState(false);
  const [prestitoTab, setPrestitoTab] = useState("request"); // "request" | "assign"
  const [prestitoSelectedAutoId, setPrestitoSelectedAutoId] = useState(null);
  const [prestitoSelectedDate, setPrestitoSelectedDate] = useState(
    () => new Date(),
  );
  const [prestitoResult, setPrestitoResult] = useState(null);
  const [prestitoAssignAutoId, setPrestitoAssignAutoId] = useState(null);
  const [prestitoAssignDate, setPrestitoAssignDate] = useState(
    () => new Date(),
  );
  const [prestitoAssignSettore, setPrestitoAssignSettore] = useState(null);
  const [prestitoAssignNote, setPrestitoAssignNote] = useState("");
  const [prestitoAssignResult, setPrestitoAssignResult] = useState(null);
  const [isAssigningPrestito, setIsAssigningPrestito] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActivities, setSearchActivities] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Portal target for react-select menus
  const [portalTarget, setPortalTarget] = useState(null);

  const {
    tasks,
    taskDraft,
    setTaskDraft,
    normalizedTasks,
    completedTasksCount,
    addTask,
    updateTask,
    toggleTaskStatus,
    removeTask,
    resetTaskPlanner: resetPlanTaskPlanner,
  } = useTaskPlannerState();

  const {
    tasks: bulkTasks,
    taskDraft: bulkTaskDraft,
    setTaskDraft: setBulkTaskDraft,
    normalizedTasks: normalizedBulkTasks,
    completedTasksCount: completedBulkTasksCount,
    addTask: addBulkTask,
    updateTask: updateBulkTask,
    toggleTaskStatus: toggleBulkTaskStatus,
    removeTask: removeBulkTask,
    resetTaskPlanner: resetBulkTaskPlanner,
  } = useTaskPlannerState();

  const refreshDashboard = useCallback(() => {
    setRefreshTick((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const div = document.createElement("div");
    div.className = "pianificazione-portal";
    document.body.appendChild(div);
    setPortalTarget(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  const settoreRT = useMemo(() => {
    const me = (users || []).find((u) => u.id === user?.uid);
    return me?.anagrafica?.settore || null;
  }, [users, user]);

  const userOptions = useMemo(() => {
    const all = (users || []).map((u) => ({
      value: u.id,
      label: getUserDisplayName(u),
      settore: u.anagrafica?.settore || null,
    }));
    if (!settoreRT || settoreRT === "ROBY") return all;
    return all.filter((o) => o.settore === settoreRT);
  }, [users, settoreRT]);

  const settoreOptions = useMemo(() => {
    const set = new Set();
    (users || []).forEach((u) => {
      const s = (u?.anagrafica?.settore ?? "").toString().trim();
      if (s) set.add(s);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }))
      .map((s) => ({ value: s, label: s }));
  }, [users]);

  const projectOptions = useMemo(() => {
    const nonArchived = (projects || []).filter((p) => p && !p.archived);
    const all = nonArchived.map((p) => ({
      value: p.id,
      label: p.nome || p.codice || p.id,
      categoria: p.categoria || null,
    }));
    if (!settoreRT || settoreRT === "ROBY" || showAllProjects) return all;
    return all.filter((o) => o.categoria === settoreRT);
  }, [projects, settoreRT, showAllProjects]);

  const sectorUsers = useMemo(() => {
    const all = (users || []).filter((u) => !!u);
    if (!settoreRT || settoreRT === "ROBY") return all;
    return all.filter((u) => u?.anagrafica?.settore === settoreRT);
  }, [users, settoreRT]);

  const filteredSectorUsers = useMemo(() => {
    return sectorUsers;
  }, [sectorUsers]);

  const sectorUserIds = useMemo(
    () => new Set((sectorUsers || []).map((u) => u.id)),
    [sectorUsers],
  );

  const userNamesById = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      map.set(u.id, getUserDisplayName(u));
    });
    return map;
  }, [users]);

  // Helper for week days
  const weekDays = useMemo(() => {
    if (viewMode !== "weekly") return [];
    const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate, viewMode]);

  const sortedSectorUsers = useMemo(() => {
    const arr = [...filteredSectorUsers];
    const collator = new Intl.Collator("it", {
      sensitivity: "base",
      numeric: true,
    });

    arr.sort((a, b) => {
      // In weekly view, sort by name only
      if (viewMode === "weekly") {
        const nameA = getUserDisplayName(a);
        const nameB = getUserDisplayName(b);
        return nameA.localeCompare(nameB);
      }

      // In daily view (Table), use sortConfig
      const assA = dashAssigned.get(a.id);
      const assB = dashAssigned.get(b.id);

      let valA = "";
      let valB = "";

      switch (sortConfig.key) {
        case "dipendente":
          valA = getUserDisplayName(a);
          valB = getUserDisplayName(b);
          break;
        case "stato":
          valA = assA
            ? assA.status === "assenza"
              ? "Assenza"
              : "Assegnato"
            : "Da assegnare";
          valB = assB
            ? assB.status === "assenza"
              ? "Assenza"
              : "Assegnato"
            : "Da assegnare";
          break;
        case "cantiere":
          valA = assA?.project?.nome || assA?.project?.codice || "";
          valB = assB?.project?.nome || assB?.project?.codice || "";
          break;
        case "mezzo":
          valA = assA?.mezzo?.targa || assA?.mezzo?.modello || "";
          valB = assB?.mezzo?.targa || assB?.mezzo?.modello || "";
          break;
        default:
          valA = "";
          valB = "";
      }

      let cmp = collator.compare(valA, valB);
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filteredSectorUsers, dashAssigned, viewMode, sortConfig]);

  const handleHeaderSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const viewRange = useMemo(() => {
    if (viewMode === "weekly") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return {
        start,
        end,
        startTs: timestamp.fromDate(new Date(start.setHours(0, 0, 0, 0))),
        endTs: timestamp.fromDate(new Date(end.setHours(23, 59, 59, 999))),
      };
    }
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return {
      start,
      end,
      startTs: timestamp.fromDate(start),
      endTs: timestamp.fromDate(end),
    };
  }, [currentDate, viewMode]);

  const prestitiFetchRange = useMemo(() => {
    const ranges = [];
    if (viewRange?.start && viewRange?.end) {
      ranges.push({ start: viewRange.start, end: addDays(viewRange.end, 7) });
    }
    if (planOpen && selectedDate) {
      ranges.push({ start: selectedDate, end: selectedDate });
    }
    if (prestitoOpen && prestitoSelectedDate) {
      ranges.push({ start: prestitoSelectedDate, end: prestitoSelectedDate });
    }
    if (prestitoOpen && prestitoAssignDate) {
      ranges.push({ start: prestitoAssignDate, end: prestitoAssignDate });
    }
    if (bulkOpen && bulkStartDate) {
      const start = bulkStartDate;
      const end =
        bulkEndDate && bulkEndDate >= bulkStartDate
          ? bulkEndDate
          : bulkStartDate;
      ranges.push({ start, end });
    }
    if (ranges.length === 0) return null;

    const minStartMs = Math.min(...ranges.map((r) => r.start.getTime()));
    const maxEndMs = Math.max(...ranges.map((r) => r.end.getTime()));
    const minStart = new Date(minStartMs);
    const maxEnd = new Date(maxEndMs);
    minStart.setHours(0, 0, 0, 0);
    maxEnd.setHours(23, 59, 59, 999);

    return {
      startTs: timestamp.fromDate(minStart),
      endTs: timestamp.fromDate(maxEnd),
    };
  }, [
    viewRange,
    planOpen,
    selectedDate,
    prestitoOpen,
    prestitoSelectedDate,
    prestitoAssignDate,
    bulkOpen,
    bulkStartDate,
    bulkEndDate,
  ]);

  useEffect(() => {
    const loadPrestiti = async () => {
      if (!settoreRT) {
        setAutoPrestiti([]);
        return;
      }
      if (!prestitiFetchRange) {
        setAutoPrestiti([]);
        return;
      }
      setPrestitiLoading(true);
      setPrestitiError("");
      try {
        const snap = await database
          .collection(AUTO_PRESTITI_COLLECTION)
          .where("date", ">=", prestitiFetchRange.startTs)
          .where("date", "<=", prestitiFetchRange.endTs)
          .get();

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (r) =>
              r?.ownerSettore === settoreRT ||
              r?.requesterSettore === settoreRT,
          );

        rows.sort((a, b) => {
          const da = a?.date?.toDate ? a.date.toDate().getTime() : 0;
          const db = b?.date?.toDate ? b.date.toDate().getTime() : 0;
          return da - db;
        });

        setAutoPrestiti(rows);
      } catch (e) {
        setPrestitiError(e?.message || "Errore caricamento prestiti automezzi");
        setAutoPrestiti([]);
      } finally {
        setPrestitiLoading(false);
      }
    };

    loadPrestiti();
  }, [settoreRT, prestitiFetchRange]);

  const blockedAutoDatesById = useMemo(() => {
    const map = new Map();
    if (!settoreRT) return map;
    (autoPrestiti || []).forEach((r) => {
      if (r?.status !== "approved") return;
      if (r?.ownerSettore !== settoreRT) return;
      const id = r?.automezzoId;
      if (!id) return;
      const dateStr = r?.date?.toDate ? getDateStr(r.date.toDate()) : "";
      if (!dateStr) return;
      if (!map.has(id)) map.set(id, new Set());
      map.get(id).add(dateStr);
    });
    return map;
  }, [autoPrestiti, settoreRT]);

  const isAutoBlockedForDate = useCallback(
    (autoId, date) => {
      if (!autoId || !date) return false;
      const set = blockedAutoDatesById.get(autoId);
      if (!set) return false;
      return set.has(getDateStr(date));
    },
    [blockedAutoDatesById],
  );

  const autoOptions = useMemo(() => {
    return (automezzi || []).map((m) => ({
      value: m.id,
      label: m.targa ? `${m.modello || ""} ${m.targa}` : m.modello || m.id,
      settoreNome: m?.settoreNome || null,
    }));
  }, [automezzi]);

  const prestitoAutoOptions = useMemo(() => {
    return (autoOptions || []).map((o) => ({
      ...o,
      label: o?.settoreNome ? `${o.label} · ${o.settoreNome}` : o.label,
    }));
  }, [autoOptions]);

  const prestitoAssignAutoOptions = useMemo(() => {
    if (!settoreRT) return [];
    if (settoreRT === "ROBY") return prestitoAutoOptions;
    return (prestitoAutoOptions || []).filter(
      (o) => o?.settoreNome === settoreRT,
    );
  }, [prestitoAutoOptions, settoreRT]);

  const automezziById = useMemo(() => {
    const m = new Map();
    (automezzi || []).forEach((doc) => m.set(doc.id, doc));
    return m;
  }, [automezzi]);

  const planAutoOptions = useMemo(() => {
    const date = selectedDate || currentDate;
    if (!settoreRT || settoreRT === "ROBY") return autoOptions;
    return autoOptions.filter((o) => {
      if (selectedAutoId && o.value === selectedAutoId) return true;
      if (o.settoreNome !== settoreRT) return true;
      return !isAutoBlockedForDate(o.value, date);
    });
  }, [
    autoOptions,
    currentDate,
    isAutoBlockedForDate,
    selectedAutoId,
    selectedDate,
    settoreRT,
  ]);

  const bulkAutoOptions = useMemo(() => {
    const start = bulkStartDate || currentDate;
    const end =
      bulkEndDate && bulkEndDate >= start
        ? bulkEndDate
        : bulkStartDate || currentDate;
    if (!settoreRT || settoreRT === "ROBY") return autoOptions;

    const rangeDays = eachDayOfInterval({ start, end });
    return autoOptions.filter((o) => {
      if (bulkSelectedAutoId && o.value === bulkSelectedAutoId) return true;
      if (o.settoreNome !== settoreRT) return true;
      return !rangeDays.some((d) => isAutoBlockedForDate(o.value, d));
    });
  }, [
    autoOptions,
    bulkEndDate,
    isAutoBlockedForDate,
    bulkSelectedAutoId,
    bulkStartDate,
    currentDate,
    settoreRT,
  ]);

  const prestitoSelectedAuto = useMemo(() => {
    if (!prestitoSelectedAutoId) return null;
    return automezziById.get(prestitoSelectedAutoId) || null;
  }, [prestitoSelectedAutoId, automezziById]);

  const prestitoSelectedOwnerSettore =
    prestitoSelectedAuto?.settoreNome || null;

  const prestitoSelectedDateStr = useMemo(() => {
    if (!prestitoSelectedDate) return null;
    return getDateStr(prestitoSelectedDate);
  }, [prestitoSelectedDate]);

  const existingPrestitoForModal = useMemo(() => {
    if (!prestitoSelectedAutoId || !prestitoSelectedDateStr) return null;
    return (
      (autoPrestiti || []).find(
        (r) =>
          r?.automezzoId === prestitoSelectedAutoId &&
          isSameDateTs(r?.date, prestitoSelectedDateStr),
      ) || null
    );
  }, [autoPrestiti, prestitoSelectedAutoId, prestitoSelectedDateStr]);

  const prestitoAssignAuto = useMemo(() => {
    if (!prestitoAssignAutoId) return null;
    return automezziById.get(prestitoAssignAutoId) || null;
  }, [prestitoAssignAutoId, automezziById]);

  const prestitoAssignDateStr = useMemo(() => {
    if (!prestitoAssignDate) return null;
    return getDateStr(prestitoAssignDate);
  }, [prestitoAssignDate]);

  const existingPrestitoForAssign = useMemo(() => {
    if (!prestitoAssignAutoId || !prestitoAssignDateStr) return null;
    return (
      (autoPrestiti || []).find(
        (r) =>
          r?.automezzoId === prestitoAssignAutoId &&
          isSameDateTs(r?.date, prestitoAssignDateStr),
      ) || null
    );
  }, [autoPrestiti, prestitoAssignAutoId, prestitoAssignDateStr]);

  const incomingPrestitoRequests = useMemo(() => {
    if (!settoreRT) return [];
    return (autoPrestiti || []).filter(
      (r) => r?.ownerSettore === settoreRT && r?.status === "requested",
    );
  }, [autoPrestiti, settoreRT]);

  const lentOutApproved = useMemo(() => {
    if (!settoreRT) return [];
    return (autoPrestiti || []).filter(
      (r) => r?.ownerSettore === settoreRT && r?.status === "approved",
    );
  }, [autoPrestiti, settoreRT]);

  const myPrestitoRequests = useMemo(() => {
    if (!settoreRT) return [];
    return (autoPrestiti || []).filter(
      (r) => r?.requesterSettore === settoreRT && r?.ownerSettore !== settoreRT,
    );
  }, [autoPrestiti, settoreRT]);

  const planSelectedDateStr = useMemo(() => {
    if (!selectedDate) return null;
    return getDateStr(selectedDate);
  }, [selectedDate]);

  const isSelectedAutoBorrowedForDate = useMemo(() => {
    if (!settoreRT || !selectedAutoId || !planSelectedDateStr) return false;
    return (autoPrestiti || []).some(
      (r) =>
        r?.status === "approved" &&
        r?.requesterSettore === settoreRT &&
        r?.automezzoId === selectedAutoId &&
        isSameDateTs(r?.date, planSelectedDateStr),
    );
  }, [autoPrestiti, planSelectedDateStr, selectedAutoId, settoreRT]);

  useEffect(() => {
    if (selectedAutoId && settoreRT) {
      if (settoreRT === "ROBY") {
        setAutoWarning((prev) => (prev === "" ? prev : ""));
        return;
      }
      const m = automezziById.get(selectedAutoId);
      const autoSettore = m?.settoreNome || null;
      if (autoSettore && autoSettore !== settoreRT) {
        if (prestitiLoading || isSelectedAutoBorrowedForDate) {
          setAutoWarning((prev) => (prev === "" ? prev : ""));
          return;
        }
        const nextWarning = `Attenzione: il mezzo selezionato appartiene al settore "${autoSettore}", diverso dal tuo "${settoreRT}".`;
        if (debugAutoSelect) {
          console.log("[PianificazioneTecnico] autoWarning mismatch", {
            selectedAutoId,
            settoreRT,
            autoSettore,
            nextWarning,
          });
        }
        setAutoWarning((prev) => (prev === nextWarning ? prev : nextWarning));
      } else {
        if (debugAutoSelect) {
          console.log("[PianificazioneTecnico] autoWarning ok", {
            selectedAutoId,
            settoreRT,
            autoSettore,
          });
        }
        setAutoWarning((prev) => (prev === "" ? prev : ""));
      }
    } else {
      if (debugAutoSelect) {
        console.log("[PianificazioneTecnico] autoWarning reset", {
          selectedAutoId,
          settoreRT,
        });
      }
      setAutoWarning((prev) => (prev === "" ? prev : ""));
    }
  }, [
    selectedAutoId,
    automezziById,
    settoreRT,
    debugAutoSelect,
    prestitiLoading,
    isSelectedAutoBorrowedForDate,
  ]);

  // Gestione blocco scroll quando il modal è aperto
  useEffect(() => {
    if (planOpen || bulkOpen || prestitoOpen || searchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [planOpen, bulkOpen, prestitoOpen, searchOpen]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    let isCancelled = false;

    const loadSearchActivities = async () => {
      if (sectorUserIds.size === 0) {
        setSearchActivities([]);
        setSearchError("");
        return;
      }

      setSearchLoading(true);
      setSearchError("");
      try {
        const snap = await database.collection("attivita").get();
        if (isCancelled) return;

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (activity) =>
              sectorUserIds.has(activity?.userId) &&
              activity?.status !== "assenza" &&
              activity?.status !== "ok",
          )
          .sort((a, b) => {
            const aTime = a?.date?.toDate ? a.date.toDate().getTime() : 0;
            const bTime = b?.date?.toDate ? b.date.toDate().getTime() : 0;
            return bTime - aTime;
          });

        setSearchActivities(rows);
      } catch (error) {
        if (isCancelled) return;
        setSearchActivities([]);
        setSearchError(
          error?.message || "Impossibile caricare le attività pianificate.",
        );
      } finally {
        if (!isCancelled) {
          setSearchLoading(false);
        }
      }
    };

    loadSearchActivities();

    return () => {
      isCancelled = true;
    };
  }, [searchOpen, sectorUserIds]);

  const normalizedSearchQuery = useMemo(
    () => normalizeSearchText(searchQuery),
    [searchQuery],
  );

  const shouldRenderSearchResults =
    normalizedSearchQuery.length >= MIN_ACTIVITY_SEARCH_LENGTH;

  const filteredSearchActivities = useMemo(() => {
    if (!shouldRenderSearchResults) return [];

    return (searchActivities || []).filter((activity) => {
      const projectName = activity?.project?.nome || "";
      const activityNote = activity?.note || "";
      const activityTasks = formatTasksInline(activity?.tasks);
      const searchableText = normalizeSearchText(
        `${projectName} ${activityNote} ${activityTasks}`,
      );

      return searchableText.includes(normalizedSearchQuery);
    });
  }, [normalizedSearchQuery, searchActivities, shouldRenderSearchResults]);

  useEffect(() => {
    const loadDash = async () => {
      setDashLoading(true);
      setDashError("");
      try {
        if (viewMode === "daily") {
          const dateStr = format(currentDate, "yyyy-MM-dd");
          const dateTs = timestamp.fromDate(new Date(`${dateStr}T00:00:00`));
          const snap = await database
            .collection("attivita")
            .where("date", "==", dateTs)
            .get();
          const map = new Map();
          snap.docs.forEach((d) => {
            const data = d.data();
            map.set(data.userId, { id: d.id, ...data });
          });
          setDashAssigned(map);
        } else {
          // Weekly fetch
          const start = startOfWeek(currentDate, { weekStartsOn: 1 });
          const end = endOfWeek(currentDate, { weekStartsOn: 1 });
          // Ensure we cover the full day of end date
          const startTs = timestamp.fromDate(
            new Date(start.setHours(0, 0, 0, 0)),
          );
          const endTs = timestamp.fromDate(
            new Date(end.setHours(23, 59, 59, 999)),
          );

          const snap = await database
            .collection("attivita")
            .where("date", ">=", startTs)
            .where("date", "<=", endTs)
            .get();

          const userMap = new Map(); // Map<userId, Map<dateStr, assignment>>
          snap.docs.forEach((d) => {
            const data = d.data();
            const dateStr = format(data.date.toDate(), "yyyy-MM-dd");
            if (!userMap.has(data.userId)) {
              userMap.set(data.userId, new Map());
            }
            userMap.get(data.userId).set(dateStr, { id: d.id, ...data });
          });
          setWeeklyAssigned(userMap);
        }
      } catch (e) {
        setDashError(e.message || String(e));
        if (viewMode === "daily") setDashAssigned(new Map());
        else setWeeklyAssigned(new Map());
      } finally {
        setDashLoading(false);
      }
    };
    loadDash();
  }, [currentDate, viewMode, refreshTick]);

  const approvePrestito = async (rowId, status) => {
    try {
      await database
        .collection(AUTO_PRESTITI_COLLECTION)
        .doc(rowId)
        .set(
          {
            status,
            decidedAt: timestamp.fromDate(new Date()),
            decidedBy: user?.uid || "",
            decidedByName: user?.displayName || user?.email || "",
          },
          { merge: true },
        );
      refreshDashboard();
    } catch {
      alert("Impossibile aggiornare la richiesta di prestito.");
    }
  };

  const cancelPrestito = async (rowId) => {
    try {
      await database
        .collection(AUTO_PRESTITI_COLLECTION)
        .doc(rowId)
        .set(
          {
            status: "cancelled",
            cancelledAt: timestamp.fromDate(new Date()),
            cancelledBy: user?.uid || "",
            cancelledByName: user?.displayName || user?.email || "",
          },
          { merge: true },
        );
      refreshDashboard();
    } catch {
      alert("Impossibile annullare il prestito.");
    }
  };

  const deletePrestito = async (rowId) => {
    if (!rowId) return;
    const ok = window.confirm(
      "Vuoi eliminare definitivamente questa richiesta?",
    );
    if (!ok) return;
    try {
      await database.collection(AUTO_PRESTITI_COLLECTION).doc(rowId).delete();
      refreshDashboard();
    } catch {
      alert("Impossibile eliminare la richiesta.");
    }
  };

  const requestPrestito = async () => {
    if (!settoreRT || !prestitoSelectedAutoId || !prestitoSelectedDate) return;
    if (settoreRT === "ROBY") return;

    const m = automezziById.get(prestitoSelectedAutoId);
    const ownerSettore = m?.settoreNome || "";
    if (!ownerSettore) {
      setPrestitoResult({
        ok: false,
        error: "Settore proprietario non disponibile.",
      });
      return;
    }
    if (ownerSettore === settoreRT) {
      setPrestitoResult({
        ok: false,
        error:
          "Il mezzo selezionato è già del tuo settore: non serve richiedere un prestito.",
      });
      return;
    }

    const dateStr = getDateStr(prestitoSelectedDate);
    const docId = `${prestitoSelectedAutoId}_${dateStr}`;

    setIsRequestingPrestito(true);
    setPrestitoResult(null);
    try {
      await database
        .collection(AUTO_PRESTITI_COLLECTION)
        .doc(docId)
        .set(
          {
            automezzoId: prestitoSelectedAutoId,
            automezzoLabel: m?.targa
              ? `${m?.modello || ""} ${m?.targa}`
              : m?.modello || prestitoSelectedAutoId,
            date: toDateTs(prestitoSelectedDate),
            ownerSettore,
            requesterSettore: settoreRT,
            status: "requested",
            requestedAt: timestamp.fromDate(new Date()),
            requestedBy: user?.uid || "",
            requestedByName: user?.displayName || user?.email || "",
            note: (prestitoNote || "").toString().trim() || "",
          },
          { merge: true },
        );
      setPrestitoNote("");
      refreshDashboard();
      setPrestitoResult({ ok: true });
    } catch {
      setPrestitoResult({
        ok: false,
        error: "Impossibile inviare la richiesta.",
      });
    } finally {
      setIsRequestingPrestito(false);
    }
  };

  const assignPrestitoDirect = async () => {
    if (!settoreRT || settoreRT === "ROBY") {
      setPrestitoAssignResult({
        ok: false,
        error: "Operazione non disponibile per questo utente.",
      });
      return;
    }
    if (!prestitoAssignAutoId || !prestitoAssignDate) return;
    const targetSettore = (prestitoAssignSettore?.value ?? "")
      .toString()
      .trim();
    if (!targetSettore || targetSettore === settoreRT) {
      setPrestitoAssignResult({
        ok: false,
        error: "Seleziona un settore diverso dal tuo.",
      });
      return;
    }
    const m = prestitoAssignAuto;
    const ownerSettore = (m?.settoreNome ?? "").toString().trim();
    if (!ownerSettore || ownerSettore !== settoreRT) {
      setPrestitoAssignResult({
        ok: false,
        error: "Puoi assegnare solo veicoli del tuo settore.",
      });
      return;
    }
    if (existingPrestitoForAssign?.status === "approved") {
      setPrestitoAssignResult({
        ok: false,
        error:
          "Esiste già un prestito approvato per questo veicolo e questa data.",
      });
      return;
    }

    const dateStr = getDateStr(prestitoAssignDate);
    const docId = `${prestitoAssignAutoId}_${dateStr}`;
    const nowTs = timestamp.fromDate(new Date());

    setIsAssigningPrestito(true);
    setPrestitoAssignResult(null);
    try {
      await database
        .collection(AUTO_PRESTITI_COLLECTION)
        .doc(docId)
        .set(
          {
            automezzoId: prestitoAssignAutoId,
            automezzoLabel: m?.targa
              ? `${m?.modello || ""} ${m?.targa}`
              : m?.modello || prestitoAssignAutoId,
            date: toDateTs(prestitoAssignDate),
            ownerSettore: settoreRT,
            requesterSettore: targetSettore,
            status: "approved",
            requestedAt: nowTs,
            requestedBy: user?.uid || "",
            requestedByName: user?.displayName || user?.email || "",
            decidedAt: nowTs,
            decidedBy: user?.uid || "",
            decidedByName: user?.displayName || user?.email || "",
            note: (prestitoAssignNote || "").toString().trim() || "",
          },
          { merge: true },
        );
      setPrestitoAssignNote("");
      refreshDashboard();
      setPrestitoAssignResult({ ok: true });
    } catch {
      setPrestitoAssignResult({
        ok: false,
        error: "Impossibile assegnare il veicolo.",
      });
    } finally {
      setIsAssigningPrestito(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser?.value || !selectedDate) return;
    if (!isAssenza && !selectedProject?.value) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      const normalizedTasks = isAssenza ? [] : normalizeActivityTasks(tasks);
      if (!isAssenza && selectedAutoId) {
        const m = automezziById.get(selectedAutoId);
        const ownerSettore = m?.settoreNome || null;
        if (ownerSettore && ownerSettore === settoreRT && prestitiLoading) {
          throw new Error(
            "Attendere il caricamento dei prestiti automezzi prima di pianificare con questo mezzo.",
          );
        }
        if (
          ownerSettore &&
          ownerSettore === settoreRT &&
          isAutoBlockedForDate(selectedAutoId, selectedDate)
        ) {
          throw new Error(
            "Mezzo non disponibile: risulta prestato ad altro settore per la data selezionata.",
          );
        }
      }
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const dateTs = timestamp.fromDate(new Date(`${dateStr}T00:00:00`));
      let p = null;
      if (!isAssenza) {
        const projectSnap = await database
          .collection("projects")
          .doc(selectedProject.value)
          .get();
        if (!projectSnap.exists) {
          throw new Error("Progetto non trovato");
        }
        p = projectSnap.data() || {};
      }
      let mezzoData = null;
      if (!isAssenza && selectedAutoId) {
        const mSnap = await database
          .collection("automezzi")
          .doc(selectedAutoId)
          .get();
        if (!mSnap.exists) {
          throw new Error("Automezzo non trovato");
        }
        const m = mSnap.data() || {};
        mezzoData = {
          id: selectedAutoId,
          modello: m.modello,
          targa: m.targa,
        };
      }
      const docId = `${selectedUser.value}_${dateStr}`;
      const sanitizedNote = sanitizeOptionalText(note);
      const payload = {
        userId: selectedUser.value,
        date: dateTs,
        project: isAssenza
          ? null
          : { id: selectedProject.value, nome: p.nome, codice: p.codice },
        mezzo: isAssenza ? null : mezzoData,
        note: isAssenza ? null : sanitizedNote,
        tasks: isAssenza ? [] : normalizedTasks,
        status: isAssenza ? "assenza" : "pianificata",
        createdBy: user.uid,
        updatedBy: user.uid,
        updatedAt: timestamp.fromDate(new Date()),
        createdAt: timestamp.fromDate(new Date()),
      };
      const attRef = database.collection("attivita").doc(docId);
      const existing = await attRef.get();
      if (existing.exists) {
        await attRef.set(payload, { merge: true });
      } else {
        await attRef.set(payload);
      }
      setResult({ ok: true, id: docId });
      setPlanOpen(false);
      refreshDashboard();
    } catch (err) {
      setResult({ ok: false, error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPlanModal = (userId, date = currentDate) => {
    const opt = userOptions.find((o) => o.value === userId) || null;
    setSelectedUser(opt || null);

    // Determine assignment based on view mode and date
    let ass = null;
    if (viewMode === "daily") {
      ass = dashAssigned.get(userId);
    } else {
      const dateStr = format(date, "yyyy-MM-dd");
      ass = weeklyAssigned.get(userId)?.get(dateStr);
    }

    if (ass) {
      setPlanEditMode(true);
      const projOpt =
        ass.project?.id &&
        projectOptions.find((p) => p.value === ass.project.id);
      setSelectedProject(projOpt || null);
      setSelectedAutoId((prev) => {
        const next = ass.mezzo?.id || null;
        return prev === next ? prev : next;
      });
      setNote(ass.note || "");
      resetPlanTaskPlanner(ass.tasks);
      setIsAssenza(isAssignmentAbsence(ass));
    } else {
      setPlanEditMode(false);
      setSelectedProject(null);
      setSelectedAutoId((prev) => (prev === null ? prev : null));
      setNote("");
      resetPlanTaskPlanner();
      setIsAssenza(false);
    }
    setSelectedDate(date);
    setResult(null);
    setAutoWarning("");
    setPlanOpen(true);
  };
  const handlePlanOverlayClick = (e) => {
    if (e.target.classList.contains("modal-overlay")) {
      setPlanOpen(false);
    }
  };
  const selectedProjectLabel = selectedProject?.label || "Nessun cantiere";
  const selectedAutoLabel =
    planAutoOptions.find((option) => option.value === selectedAutoId)?.label ||
    "Nessun mezzo";
  const selectedDateLabel = selectedDate
    ? format(selectedDate, "EEEE dd MMMM yyyy", { locale: itLocale })
    : "Seleziona una data";
  const currentDateLabel = currentDate
    ? format(currentDate, "EEEE dd MMMM yyyy", { locale: itLocale })
    : "Seleziona una data";
  const bulkSelectedProjectLabel =
    bulkSelectedProject?.label || "Nessun cantiere";
  const bulkSelectedAutoLabel =
    bulkAutoOptions.find((option) => option.value === bulkSelectedAutoId)
      ?.label || "Nessun mezzo";
  const bulkUsersCount = bulkSelectedUsers.length;
  const bulkDateRangeLabel = bulkStartDate
    ? bulkEndDate && bulkEndDate >= bulkStartDate
      ? `${format(bulkStartDate, "dd/MM/yyyy")} - ${format(bulkEndDate, "dd/MM/yyyy")}`
      : format(bulkStartDate, "dd/MM/yyyy")
    : "Seleziona un intervallo";

  return (
    <ResTecnicoAccess>
      <div className="pianificazione-container">
        <h1>Pianificazione Attività</h1>
        <div className="dashboard">
          <div className="dashboard-header">
            <div className="dashboard-title">
              Dashboard Attività Settore
              <div className="view-selector">
                <button
                  className={`view-btn ${viewMode === "daily" ? "active" : ""}`}
                  onClick={() => setViewMode("daily")}
                >
                  Giornaliera
                </button>
                <button
                  className={`view-btn ${
                    viewMode === "weekly" ? "active" : ""
                  }`}
                  onClick={() => setViewMode("weekly")}
                >
                  Settimanale
                </button>
              </div>
            </div>
            <div className="dashboard-controls">
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={itLocale}
              >
                <DatePicker
                  value={currentDate}
                  onChange={(d) => d && setCurrentDate(d)}
                  format="yyyy-MM-dd"
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      },
                    },
                  }}
                  clearable
                />
              </LocalizationProvider>
              <div className="dash-stepper">
                <button
                  type="button"
                  className="dash-stepper-btn"
                  onClick={() =>
                    setCurrentDate(
                      viewMode === "daily"
                        ? addDays(currentDate, -1)
                        : addDays(currentDate, -7),
                    )
                  }
                >
                  −
                </button>
                <button
                  type="button"
                  className="dash-stepper-btn"
                  onClick={() =>
                    setCurrentDate(
                      viewMode === "daily"
                        ? addDays(currentDate, 1)
                        : addDays(currentDate, 7),
                    )
                  }
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className="bulk-btn"
                onClick={() => {
                  setBulkSelectedUsers([]);
                  setBulkSelectedProject(null);
                  setBulkSelectedAutoId(null);
                  setBulkNote("");
                  resetBulkTaskPlanner();
                  setBulkResult(null);
                  setBulkStartDate(currentDate);
                  setBulkEndDate(null);
                  setBulkOpen(true);
                }}
              >
                Pianificazione Massiva
              </button>
              <button
                type="button"
                className="bulk-btn"
                onClick={() => {
                  setPrestitoSelectedAutoId(null);
                  setPrestitoSelectedDate(currentDate);
                  setPrestitoNote("");
                  setPrestitoResult(null);
                  setPrestitoTab("request");
                  setPrestitoAssignAutoId(null);
                  setPrestitoAssignDate(currentDate);
                  setPrestitoAssignSettore(null);
                  setPrestitoAssignNote("");
                  setPrestitoAssignResult(null);
                  setPrestitoOpen(true);
                }}
              >
                Prestito Veicolo
              </button>
              <button
                type="button"
                className="bulk-btn"
                onClick={() => {
                  setSearchQuery("");
                  setSearchError("");
                  setSearchOpen(true);
                }}
              >
                Cerca
              </button>
            </div>
          </div>
          {dashError && <div className="result error">{dashError}</div>}
          {(prestitiLoading || prestitiError) && (
            <div
              style={{
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              {prestitiLoading && (
                <div className="result">Caricamento prestiti automezzi...</div>
              )}
              {prestitiError && (
                <div className="result error">{prestitiError}</div>
              )}
            </div>
          )}
          {!prestitiLoading &&
            !prestitiError &&
            incomingPrestitoRequests.length > 0 && (
              <div
                className="result"
                style={{ marginTop: 10, marginBottom: 10 }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Richieste prestito in arrivo
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {incomingPrestitoRequests.map((r) => {
                    const dateLabel = r?.date?.toDate
                      ? format(r.date.toDate(), "dd/MM/yyyy")
                      : "—";
                    const fromSettore = r?.requesterSettore || "—";
                    const label = r?.automezzoLabel || r?.automezzoId || "—";
                    return (
                      <div
                        key={r.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                          padding: "8px 10px",
                          background: "#fff",
                          borderRadius: 8,
                          border: "1px solid #e5e5e5",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            {dateLabel} · {label}
                          </div>
                          <div style={{ color: "#555", fontSize: 13 }}>
                            Richiedente: {fromSettore}
                            {r?.note ? ` · Note: ${r.note}` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                          <button
                            type="button"
                            className="pt-action-btn pt-action-approve"
                            onClick={() => approvePrestito(r.id, "approved")}
                          >
                            Approva
                          </button>
                          <button
                            type="button"
                            className="pt-action-btn pt-action-reject"
                            onClick={() => approvePrestito(r.id, "rejected")}
                          >
                            Rifiuta
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          {!prestitiLoading && !prestitiError && lentOutApproved.length > 0 && (
            <div className="result" style={{ marginTop: 10, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Mezzi prestati ad altri settori (bloccati nel tuo settore)
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {lentOutApproved.map((r) => {
                  const dateLabel = r?.date?.toDate
                    ? format(r.date.toDate(), "dd/MM/yyyy")
                    : "—";
                  const toSettore = r?.requesterSettore || "—";
                  const label = r?.automezzoLabel || r?.automezzoId || "—";
                  return (
                    <div
                      key={r.id}
                      style={{
                        padding: "8px 10px",
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #e5e5e5",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            {dateLabel} · {label}
                          </div>
                          <div style={{ color: "#555", fontSize: 13 }}>
                            Prestato a: {toSettore}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                          <button
                            type="button"
                            className="pt-action-btn pt-action-cancel"
                            onClick={() => cancelPrestito(r.id)}
                          >
                            Annulla
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!prestitiLoading &&
            !prestitiError &&
            myPrestitoRequests.length > 0 && (
              <div
                className="result"
                style={{ marginTop: 10, marginBottom: 10 }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  Le tue richieste prestito
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {myPrestitoRequests.map((r) => {
                    const dateLabel = r?.date?.toDate
                      ? format(r.date.toDate(), "dd/MM/yyyy")
                      : "—";
                    const label = r?.automezzoLabel || r?.automezzoId || "—";
                    const owner = r?.ownerSettore || "—";
                    const status =
                      r?.status === "requested"
                        ? "In attesa"
                        : r?.status === "approved"
                          ? "Approvata"
                          : r?.status === "rejected"
                            ? "Rifiutata"
                            : r?.status === "cancelled"
                              ? "Annullata"
                              : r?.status || "—";
                    return (
                      <div
                        key={r.id}
                        style={{
                          padding: "8px 10px",
                          background: "#fff",
                          borderRadius: 8,
                          border: "1px solid #e5e5e5",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              {dateLabel} · {label}
                            </div>
                            <div style={{ color: "#555", fontSize: 13 }}>
                              Settore proprietario: {owner} · Stato: {status}
                            </div>
                          </div>
                          {r?.status === "cancelled" && (
                            <div
                              style={{ display: "flex", gap: 8, flexShrink: 0 }}
                            >
                              <button
                                type="button"
                                className="pt-action-btn pt-action-reject"
                                onClick={() => deletePrestito(r.id)}
                              >
                                Elimina
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          {dashLoading ? (
            <div className="result">Caricamento...</div>
          ) : viewMode === "daily" ? (
            <>
              {sortedSectorUsers.length === 0 && (
                <div className="result">Nessun dipendente nel tuo settore</div>
              )}
              {sortedSectorUsers.length > 0 && (
                <div className="planning-sort-bar">
                  {[
                    { key: "dipendente", label: "Dipendente" },
                    { key: "stato", label: "Stato" },
                    { key: "cantiere", label: "Cantiere" },
                    { key: "mezzo", label: "Mezzo" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`planning-sort-btn ${
                        sortConfig.key === option.key ? "active" : ""
                      }`}
                      onClick={() => handleHeaderSort(option.key)}
                    >
                      {option.label}
                      {sortConfig.key === option.key
                        ? sortConfig.dir === "asc"
                          ? " ▲"
                          : " ▼"
                        : ""}
                    </button>
                  ))}
                </div>
              )}
              <div className="planning-mobile-list planning-cards-daily">
                {sortedSectorUsers.length === 0 && (
                  <div className="result">
                    Nessun dipendente nel tuo settore
                  </div>
                )}
                {sortedSectorUsers.map((u) => {
                  const ass = dashAssigned.get(u.id);
                  const isAssenzaStatus = isAssignmentAbsence(ass);

                  return (
                    <div
                      key={`mobile-daily-${u.id}`}
                      className="planning-mobile-card"
                    >
                      <div className="planning-mobile-card-top">
                        <div>
                          <div className="planning-mobile-card-title">
                            {getUserDisplayName(u)}
                          </div>
                          <div className="planning-mobile-card-subtitle">
                            {currentDateLabel}
                          </div>
                        </div>
                        <span
                          className={`status-badge ${getAssignmentStatusClassName(ass)}`}
                        >
                          {getAssignmentStatusLabel(ass)}
                        </span>
                      </div>
                      <div className="planning-mobile-card-body">
                        <div className="planning-mobile-field">
                          <span>Cantiere</span>
                          <strong>
                            {isAssenzaStatus
                              ? "Assenza"
                              : getProjectDisplayLabel(ass?.project)}
                          </strong>
                        </div>
                        <div className="planning-mobile-field">
                          <span>Mezzo</span>
                          <strong>{getVehicleDisplayLabel(ass?.mezzo)}</strong>
                        </div>
                        <div className="planning-mobile-field planning-mobile-field-full">
                          <span>Note / Task</span>
                          <strong>{getAssignmentNoteLabel(ass)}</strong>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="planning-mobile-action"
                        onClick={() => openPlanModal(u.id)}
                      >
                        {ass ? "Modifica pianificazione" : "Pianifica attività"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="planning-mobile-list planning-cards-weekly">
              {sortedSectorUsers.map((u) => {
                const userAssigns = weeklyAssigned.get(u.id);

                return (
                  <div
                    key={`mobile-weekly-${u.id}`}
                    className="planning-mobile-card is-weekly"
                  >
                    <div className="planning-mobile-card-top">
                      <div className="planning-mobile-card-title">
                        {getUserDisplayName(u)}
                      </div>
                    </div>
                    <div className="planning-week-grid">
                      {weekDays.map((d) => {
                        const dateStr = format(d, "yyyy-MM-dd");
                        const ass = userAssigns?.get(dateStr);
                        const isAssenzaStatus = isAssignmentAbsence(ass);

                        return (
                          <button
                            key={`week-mobile-${u.id}-${dateStr}`}
                            type="button"
                            className={`planning-week-day-btn ${
                              ass
                                ? isAssenzaStatus
                                  ? "is-assenza"
                                  : "is-assigned"
                                : ""
                            }`}
                            onClick={() => openPlanModal(u.id, d)}
                          >
                            <span className="planning-week-day-label">
                              {format(d, "EEE dd/MM", { locale: itLocale })}
                            </span>
                            <strong>
                              {ass
                                ? isAssenzaStatus
                                  ? "Assenza"
                                  : getProjectDisplayLabel(ass?.project)
                                : "Pianifica"}
                            </strong>
                            {ass?.mezzo && !isAssenzaStatus && (
                              <small>{getVehicleDisplayLabel(ass.mezzo)}</small>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {planOpen && (
          <div className="modal-overlay" onClick={handlePlanOverlayClick}>
            <div
              className="modal-content modal-content-plan"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Pianifica Attività</h2>
                <button
                  className="close-btn"
                  onClick={() => setPlanOpen(false)}
                >
                  ×
                </button>
              </div>
              {planEditMode && (
                <div className="modal-subtitle edit">
                  Modifica pianificazione esistente
                </div>
              )}
              <form
                className="pianificazione-form pianificazione-form-plan"
                onSubmit={handleSubmit}
              >
                <div className="plan-hero">
                  <div>
                    <div className="plan-kicker">
                      {planEditMode ? "Attività esistente" : "Nuova attività"}
                    </div>
                    <div className="plan-title">
                      {selectedUser?.label || "Seleziona dipendente"}
                    </div>
                    <div className="plan-subtitle">{selectedDateLabel}</div>
                  </div>
                  <label
                    className={`plan-absence-toggle ${
                      isAssenza ? "active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isAssenza}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsAssenza(checked);
                        if (checked) {
                          setSelectedProject(null);
                          setSelectedAutoId(null);
                          setNote("");
                          resetPlanTaskPlanner();
                        }
                      }}
                    />
                    <span>Segnala assenza</span>
                  </label>
                </div>

                <SummaryCards
                  cards={[
                    {
                      label: "Stato",
                      value: isAssenza
                        ? "Assenza segnalata"
                        : "Attività pianificata",
                    },
                    {
                      label: "Cantiere",
                      value: isAssenza ? "Non previsto" : selectedProjectLabel,
                    },
                    {
                      label: "Mezzo",
                      value: isAssenza ? "Non previsto" : selectedAutoLabel,
                    },
                    {
                      label: "Task",
                      value: `${completedTasksCount}/${normalizedTasks.length}`,
                    },
                  ]}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Dettagli attività</h3>
                      <p>Seleziona dipendente, data e risorse operative.</p>
                    </div>
                  </div>
                  <div className="modal-section-grid modal-section-grid-plan">
                    <div className="form-row">
                      <label>Dipendente</label>
                      <Select
                        classNamePrefix="react-select"
                        value={selectedUser}
                        onChange={(opt) => setSelectedUser(opt || null)}
                        options={userOptions}
                        placeholder="Seleziona dipendente"
                        isSearchable
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                    <div className="form-row">
                      <label>Data</label>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={itLocale}
                      >
                        <DatePicker
                          value={selectedDate}
                          onChange={(d) => d && setSelectedDate(d)}
                          format="yyyy-MM-dd"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                },
                              },
                            },
                          }}
                          clearable
                        />
                      </LocalizationProvider>
                    </div>
                    <div
                      className={`form-row ${isAssenza ? "is-disabled" : ""}`}
                    >
                      <div className="field-header">
                        <label>Progetto</label>
                        <label className="field-inline-toggle">
                          <input
                            type="checkbox"
                            checked={showAllProjects}
                            onChange={(e) =>
                              setShowAllProjects(e.target.checked)
                            }
                            disabled={isAssenza}
                          />
                          <span>Mostra tutti i cantieri</span>
                        </label>
                      </div>
                      <Select
                        classNamePrefix="react-select"
                        value={selectedProject}
                        onChange={(opt) => setSelectedProject(opt || null)}
                        options={projectOptions}
                        placeholder="Seleziona progetto"
                        isSearchable
                        isDisabled={isAssenza}
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                    <div
                      className={`form-row ${isAssenza ? "is-disabled" : ""}`}
                    >
                      <label>Automezzo</label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          selectedAutoId
                            ? planAutoOptions.find(
                                (option) => option.value === selectedAutoId,
                              ) || null
                            : null
                        }
                        onChange={(opt, actionMeta) => {
                          const next = opt?.value || null;
                          if (debugAutoSelect) {
                            console.log(
                              "[PianificazioneTecnico] select automezzo onChange",
                              {
                                action: actionMeta?.action,
                                prev: selectedAutoId,
                                next,
                                option: opt,
                              },
                            );
                          }
                          setSelectedAutoId((prev) =>
                            prev === next ? prev : next,
                          );
                        }}
                        options={planAutoOptions}
                        placeholder="Seleziona mezzo (opzionale)"
                        isClearable
                        isSearchable
                        isDisabled={isAssenza}
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                      {autoWarning && (
                        <div className="result error" style={{ marginTop: 8 }}>
                          {autoWarning}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <TaskPlannerSection
                  isDisabled={isAssenza}
                  tasks={tasks}
                  taskDraft={taskDraft}
                  setTaskDraft={setTaskDraft}
                  addTask={addTask}
                  updateTask={updateTask}
                  toggleTaskStatus={toggleTaskStatus}
                  removeTask={removeTask}
                  completedTasksCount={completedTasksCount}
                  normalizedTasks={normalizedTasks}
                />

                <div
                  className={`modal-section-card ${
                    isAssenza ? "section-card-disabled" : ""
                  }`}
                >
                  <div className="plan-section-head">
                    <div>
                      <h3>Note attività</h3>
                      <p>
                        Inserisci dettagli utili, vincoli o indicazioni
                        operative.
                      </p>
                    </div>
                  </div>
                  <div className="form-row form-row-no-margin">
                    <label>Note</label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={4}
                      placeholder="Accessi, materiali, contatti, orari o altre informazioni utili."
                      disabled={isAssenza}
                    />
                  </div>
                </div>

                <div className="form-actions form-actions-plan">
                  <button type="button" onClick={() => setPlanOpen(false)}>
                    Annulla
                  </button>
                  <button type="submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? "Salvataggio..."
                      : planEditMode
                        ? "Aggiorna attività"
                        : "Salva attività"}
                  </button>
                </div>
                {result && (
                  <div className={result.ok ? "result ok" : "result error"}>
                    {result.ok
                      ? "Attività pianificata"
                      : `Errore: ${result.error}`}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
        {bulkOpen && (
          <div
            className="modal-overlay"
            onClick={(e) =>
              e.target.classList.contains("modal-overlay") && setBulkOpen(false)
            }
          >
            <div
              className="modal-content modal-content-plan"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Pianifica Massivamente</h2>
                <button
                  className="close-btn"
                  onClick={() => setBulkOpen(false)}
                >
                  ×
                </button>
              </div>
              <form
                className="pianificazione-form pianificazione-form-plan"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setBulkSubmitting(true);
                  setBulkResult(null);
                  try {
                    const usersSel = bulkSelectedUsers || [];
                    const hasUsers = usersSel.length > 0;
                    const hasProject = !!bulkSelectedProject;
                    const hasAuto = !!bulkSelectedAutoId;
                    const hasNote = !!bulkNote.trim();
                    const hasTasks = normalizedBulkTasks.length > 0;
                    if (
                      !hasUsers ||
                      !(hasProject || hasAuto || hasNote || hasTasks)
                    ) {
                      throw new Error(
                        "Seleziona almeno un dipendente e almeno uno tra cantiere, mezzo, note o task",
                      );
                    }
                    const startD = bulkStartDate || currentDate;
                    const endD =
                      bulkEndDate && bulkEndDate >= startD
                        ? bulkEndDate
                        : startD;
                    const days =
                      endD && startD
                        ? eachDayOfInterval({ start: startD, end: endD })
                        : [startD];
                    const nowTs = timestamp.fromDate(new Date());
                    const proj = hasProject
                      ? (projects || []).find(
                          (p) => p.id === bulkSelectedProject.value,
                        ) || null
                      : null;
                    const auto = hasAuto
                      ? automezziById.get(bulkSelectedAutoId) || null
                      : null;
                    if (hasAuto && settoreRT && settoreRT !== "ROBY") {
                      const ownerSettore = auto?.settoreNome || null;
                      if (
                        ownerSettore &&
                        ownerSettore === settoreRT &&
                        prestitiLoading
                      ) {
                        throw new Error(
                          "Attendere il caricamento dei prestiti automezzi prima di pianificare con questo mezzo.",
                        );
                      }
                      if (
                        ownerSettore &&
                        ownerSettore === settoreRT &&
                        days.some((d) =>
                          isAutoBlockedForDate(bulkSelectedAutoId, d),
                        )
                      ) {
                        throw new Error(
                          "Mezzo non disponibile: risulta prestato ad altro settore in almeno uno dei giorni selezionati.",
                        );
                      }
                    }
                    for (const day of days) {
                      const dateStr = format(day, "yyyy-MM-dd");
                      const dayStart = new Date(
                        day.getFullYear(),
                        day.getMonth(),
                        day.getDate(),
                        0,
                        0,
                        0,
                        0,
                      );
                      const dateTs = timestamp.fromDate(dayStart);
                      for (const uOpt of usersSel) {
                        const docId = `${uOpt.value}_${dateStr}`;
                        const attRef = database
                          .collection("attivita")
                          .doc(docId);
                        const existing = await attRef.get();
                        const payload = {
                          userId: uOpt.value,
                          date: dateTs,
                          updatedBy: user.uid,
                          updatedAt: nowTs,
                        };
                        if (!existing.exists) {
                          payload.createdBy = user.uid;
                          payload.createdAt = nowTs;
                        }
                        if (hasProject) {
                          payload.project = {
                            id: bulkSelectedProject.value,
                            nome: proj?.nome,
                            codice: proj?.codice,
                          };
                          payload.status = "pianificata";
                        }
                        if (hasAuto) {
                          payload.mezzo = {
                            id: bulkSelectedAutoId,
                            modello: auto?.modello,
                            targa: auto?.targa,
                          };
                          payload.status = "pianificata";
                        }
                        if (hasNote) {
                          payload.note = sanitizeOptionalText(bulkNote);
                          payload.status = "pianificata";
                        }
                        if (hasTasks) {
                          payload.tasks = normalizedBulkTasks;
                          payload.status = "pianificata";
                        }
                        await attRef.set(payload, { merge: true });
                      }
                    }
                    setBulkResult({ ok: true });
                    setBulkOpen(false);
                    refreshDashboard();
                  } catch (err) {
                    setBulkResult({ ok: false, error: err.message });
                  } finally {
                    setBulkSubmitting(false);
                  }
                }}
              >
                <div className="plan-hero">
                  <div>
                    <div className="plan-kicker">Pianificazione multipla</div>
                    <div className="plan-title">
                      {bulkUsersCount > 0
                        ? `${bulkUsersCount} dipendenti selezionati`
                        : "Seleziona i dipendenti"}
                    </div>
                    <div className="plan-subtitle">{bulkDateRangeLabel}</div>
                  </div>
                </div>

                <SummaryCards
                  cards={[
                    { label: "Dipendenti", value: bulkUsersCount || "0" },
                    { label: "Periodo", value: bulkDateRangeLabel },
                    { label: "Cantiere", value: bulkSelectedProjectLabel },
                    {
                      label: "Task",
                      value: `${completedBulkTasksCount}/${normalizedBulkTasks.length}`,
                    },
                  ]}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Dettagli attività</h3>
                      <p>
                        Applica la stessa pianificazione a più dipendenti e a un
                        intervallo di giorni.
                      </p>
                    </div>
                  </div>
                  <div className="modal-section-grid modal-section-grid-plan">
                    <div className="form-row">
                      <label>Data inizio</label>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={itLocale}
                      >
                        <DatePicker
                          value={bulkStartDate}
                          onChange={(d) => setBulkStartDate(d || null)}
                          format="yyyy-MM-dd"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "6px",
                                },
                              },
                            },
                          }}
                          clearable
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="form-row">
                      <label>Data fine</label>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={itLocale}
                      >
                        <DatePicker
                          value={bulkEndDate}
                          onChange={(d) => setBulkEndDate(d || null)}
                          format="yyyy-MM-dd"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "6px",
                                },
                              },
                            },
                          }}
                          clearable
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="form-row">
                      <label>Dipendenti</label>
                      <Select
                        classNamePrefix="react-select"
                        value={bulkSelectedUsers}
                        onChange={(opts) => setBulkSelectedUsers(opts || [])}
                        options={userOptions}
                        isMulti
                        isSearchable
                        placeholder="Seleziona uno o più dipendenti"
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                    <div className="form-row">
                      <div className="field-header">
                        <label>Cantiere</label>
                        <label className="field-inline-toggle">
                          <input
                            type="checkbox"
                            checked={showAllProjects}
                            onChange={(e) =>
                              setShowAllProjects(e.target.checked)
                            }
                          />
                          <span>Mostra tutti i cantieri</span>
                        </label>
                      </div>
                      <Select
                        classNamePrefix="react-select"
                        value={bulkSelectedProject}
                        onChange={(opt) => setBulkSelectedProject(opt || null)}
                        options={projectOptions}
                        isSearchable
                        isClearable
                        placeholder="Seleziona cantiere (opzionale)"
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                    <div className="form-row">
                      <label>Automezzo</label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          bulkSelectedAutoId
                            ? bulkAutoOptions.find(
                                (o) => o.value === bulkSelectedAutoId,
                              ) || null
                            : null
                        }
                        onChange={(opt) =>
                          setBulkSelectedAutoId(opt?.value || null)
                        }
                        options={bulkAutoOptions}
                        isSearchable
                        isClearable
                        placeholder="Seleziona mezzo (opzionale)"
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                  </div>
                </div>

                <SummaryCards
                  cards={[
                    {
                      label: "Stato",
                      value:
                        bulkSelectedProject ||
                        bulkSelectedAutoId ||
                        bulkNote.trim() ||
                        normalizedBulkTasks.length
                          ? "Attività pianificata"
                          : "Da completare",
                    },
                    { label: "Mezzo", value: bulkSelectedAutoLabel },
                    {
                      label: "Note",
                      value: bulkNote.trim() ? "Presenti" : "Non inserite",
                    },
                    {
                      label: "Task aperti",
                      value: `${normalizedBulkTasks.length - completedBulkTasksCount}`,
                    },
                  ]}
                />

                <TaskPlannerSection
                  tasks={bulkTasks}
                  taskDraft={bulkTaskDraft}
                  setTaskDraft={setBulkTaskDraft}
                  addTask={addBulkTask}
                  updateTask={updateBulkTask}
                  toggleTaskStatus={toggleBulkTaskStatus}
                  removeTask={removeBulkTask}
                  completedTasksCount={completedBulkTasksCount}
                  normalizedTasks={normalizedBulkTasks}
                />

                <div className="modal-section-card">
                  <div className="plan-section-head">
                    <div>
                      <h3>Note attività</h3>
                      <p>
                        Inserisci dettagli utili, vincoli o indicazioni
                        operative da applicare a tutta la selezione.
                      </p>
                    </div>
                  </div>
                  <div className="form-row">
                    <label>Note</label>
                    <textarea
                      value={bulkNote}
                      onChange={(e) => setBulkNote(e.target.value)}
                      rows={3}
                      placeholder="Note (opzionale)"
                    />
                  </div>
                </div>
                <div className="form-actions form-actions-plan">
                  <button type="button" onClick={() => setBulkOpen(false)}>
                    Chiudi
                  </button>
                  <button type="submit" disabled={bulkSubmitting}>
                    {bulkSubmitting
                      ? "Salvataggio..."
                      : "Pianifica Massivamente"}
                  </button>
                </div>
                {bulkResult && (
                  <div className={bulkResult.ok ? "result ok" : "result error"}>
                    {bulkResult.ok
                      ? "Pianificazione massiva completata"
                      : `Errore: ${bulkResult.error}`}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
        {prestitoOpen && (
          <div
            className="modal-overlay"
            onClick={(e) =>
              e.target.classList.contains("modal-overlay") &&
              setPrestitoOpen(false)
            }
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Prestito Veicolo</h2>
                <button
                  className="close-btn"
                  onClick={() => setPrestitoOpen(false)}
                >
                  ×
                </button>
              </div>
              <div style={{ padding: "0 20px 10px 20px" }}>
                <div className="view-selector">
                  <button
                    type="button"
                    className={`view-btn ${prestitoTab === "request" ? "active" : ""}`}
                    onClick={() => {
                      setPrestitoTab("request");
                      setPrestitoResult(null);
                      setPrestitoAssignResult(null);
                    }}
                  >
                    Richiesta
                  </button>
                  <button
                    type="button"
                    className={`view-btn ${prestitoTab === "assign" ? "active" : ""}`}
                    onClick={() => {
                      setPrestitoTab("assign");
                      setPrestitoResult(null);
                      setPrestitoAssignResult(null);
                    }}
                    disabled={!settoreRT || settoreRT === "ROBY"}
                    title={
                      !settoreRT || settoreRT === "ROBY"
                        ? "Non disponibile per questo utente"
                        : ""
                    }
                  >
                    Assegna
                  </button>
                </div>
              </div>
              <form
                className="pianificazione-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (prestitoTab === "assign") {
                    await assignPrestitoDirect();
                  } else {
                    await requestPrestito();
                  }
                }}
              >
                {prestitoTab === "assign" ? (
                  <div className="modal-section-grid">
                    <div className="form-row">
                      <label>Data</label>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={itLocale}
                      >
                        <DatePicker
                          value={prestitoAssignDate}
                          onChange={(d) => d && setPrestitoAssignDate(d)}
                          format="yyyy-MM-dd"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "6px",
                                },
                              },
                            },
                          }}
                          clearable
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="form-row">
                      <label>Veicolo (del tuo settore)</label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          prestitoAssignAutoId
                            ? prestitoAssignAutoOptions.find(
                                (o) => o.value === prestitoAssignAutoId,
                              ) || null
                            : null
                        }
                        onChange={(opt) =>
                          setPrestitoAssignAutoId(opt?.value || null)
                        }
                        options={prestitoAssignAutoOptions}
                        placeholder="Seleziona veicolo"
                        isClearable
                        isSearchable
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                      {existingPrestitoForAssign && (
                        <div className="result" style={{ marginTop: 8 }}>
                          Prestito esistente:{" "}
                          {existingPrestitoForAssign.status === "approved"
                            ? "Approvato"
                            : existingPrestitoForAssign.status === "requested"
                              ? "Richiesto"
                              : existingPrestitoForAssign.status === "rejected"
                                ? "Rifiutato"
                                : existingPrestitoForAssign.status ===
                                    "cancelled"
                                  ? "Annullato"
                                  : existingPrestitoForAssign.status || "—"}
                          {existingPrestitoForAssign.requesterSettore
                            ? ` · Settore: ${existingPrestitoForAssign.requesterSettore}`
                            : ""}
                        </div>
                      )}
                    </div>
                    <div className="form-row">
                      <label>Assegna a settore</label>
                      <Select
                        classNamePrefix="react-select"
                        value={prestitoAssignSettore}
                        onChange={(opt) =>
                          setPrestitoAssignSettore(opt || null)
                        }
                        options={(settoreOptions || []).filter(
                          (s) => s?.value && s.value !== settoreRT,
                        )}
                        placeholder="Seleziona settore"
                        isClearable
                        isSearchable
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                    </div>
                    <div className="form-row">
                      <label>Note assegnazione</label>
                      <textarea
                        value={prestitoAssignNote}
                        onChange={(e) => setPrestitoAssignNote(e.target.value)}
                        rows={3}
                        placeholder="Inserisci una nota (opzionale)"
                        disabled={isAssigningPrestito}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="modal-section-grid">
                    <div className="form-row">
                      <label>Data</label>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={itLocale}
                      >
                        <DatePicker
                          value={prestitoSelectedDate}
                          onChange={(d) => d && setPrestitoSelectedDate(d)}
                          format="yyyy-MM-dd"
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "6px",
                                },
                              },
                            },
                          }}
                          clearable
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="form-row">
                      <label>Veicolo</label>
                      <Select
                        classNamePrefix="react-select"
                        value={
                          prestitoSelectedAutoId
                            ? prestitoAutoOptions.find(
                                (o) => o.value === prestitoSelectedAutoId,
                              ) || null
                            : null
                        }
                        onChange={(opt) =>
                          setPrestitoSelectedAutoId(opt?.value || null)
                        }
                        options={prestitoAutoOptions}
                        placeholder="Seleziona veicolo"
                        isClearable
                        isSearchable
                        menuPortalTarget={portalTarget || undefined}
                        styles={SELECT_MENU_PORTAL_STYLES}
                      />
                      {prestitoSelectedOwnerSettore &&
                        settoreRT &&
                        prestitoSelectedOwnerSettore === settoreRT && (
                          <div
                            className="result error"
                            style={{ marginTop: 8 }}
                          >
                            Il veicolo selezionato è già del tuo settore: non
                            serve richiedere un prestito.
                          </div>
                        )}
                      {existingPrestitoForModal && (
                        <div className="result" style={{ marginTop: 8 }}>
                          Stato richiesta:{" "}
                          {existingPrestitoForModal.status === "requested"
                            ? "In attesa"
                            : existingPrestitoForModal.status === "approved"
                              ? "Approvata"
                              : existingPrestitoForModal.status === "rejected"
                                ? "Rifiutata"
                                : existingPrestitoForModal.status ===
                                    "cancelled"
                                  ? "Annullata"
                                  : existingPrestitoForModal.status || "—"}
                          {existingPrestitoForModal.note
                            ? ` · Note: ${existingPrestitoForModal.note}`
                            : ""}
                        </div>
                      )}
                    </div>
                    <div className="form-row">
                      <label>Note richiesta</label>
                      <textarea
                        value={prestitoNote}
                        onChange={(e) => setPrestitoNote(e.target.value)}
                        rows={3}
                        placeholder="Inserisci una nota (opzionale)"
                        disabled={isRequestingPrestito}
                      />
                    </div>
                  </div>
                )}
                <div
                  className="form-actions"
                  style={{ display: "flex", gap: 8 }}
                >
                  <button type="button" onClick={() => setPrestitoOpen(false)}>
                    Chiudi
                  </button>
                  {prestitoTab === "assign" ? (
                    <button
                      type="submit"
                      className="pt-action-btn pt-action-approve"
                      disabled={
                        isAssigningPrestito ||
                        !prestitoAssignAutoId ||
                        !prestitoAssignDate ||
                        !prestitoAssignSettore?.value ||
                        !settoreRT ||
                        settoreRT === "ROBY" ||
                        prestitoAssignSettore?.value === settoreRT ||
                        (prestitoAssignAuto?.settoreNome ?? "") !== settoreRT ||
                        existingPrestitoForAssign?.status === "approved"
                      }
                    >
                      {isAssigningPrestito ? "Salvataggio..." : "Assegna"}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="pt-action-btn pt-action-approve"
                      disabled={
                        isRequestingPrestito ||
                        !prestitoSelectedAutoId ||
                        !prestitoSelectedDate ||
                        !settoreRT ||
                        settoreRT === "ROBY" ||
                        (prestitoSelectedOwnerSettore &&
                          prestitoSelectedOwnerSettore === settoreRT) ||
                        existingPrestitoForModal?.status === "requested" ||
                        existingPrestitoForModal?.status === "approved"
                      }
                    >
                      {isRequestingPrestito ? "Invio..." : "Richiedi prestito"}
                    </button>
                  )}
                  {existingPrestitoForModal?.status === "cancelled" && (
                    <button
                      type="button"
                      className="pt-action-btn pt-action-reject"
                      onClick={async () => {
                        await deletePrestito(existingPrestitoForModal.id);
                        setPrestitoResult(null);
                      }}
                    >
                      Elimina richiesta
                    </button>
                  )}
                </div>
                {prestitoTab === "assign"
                  ? prestitoAssignResult && (
                      <div
                        className={
                          prestitoAssignResult.ok ? "result ok" : "result error"
                        }
                      >
                        {prestitoAssignResult.ok
                          ? "Veicolo assegnato"
                          : `Errore: ${prestitoAssignResult.error}`}
                      </div>
                    )
                  : prestitoResult && (
                      <div
                        className={
                          prestitoResult.ok ? "result ok" : "result error"
                        }
                      >
                        {prestitoResult.ok
                          ? "Richiesta inviata"
                          : `Errore: ${prestitoResult.error}`}
                      </div>
                    )}
              </form>
            </div>
          </div>
        )}
        {searchOpen && (
          <div
            className="modal-overlay"
            onClick={(e) =>
              e.target.classList.contains("modal-overlay") &&
              setSearchOpen(false)
            }
          >
            <div
              className="modal-content modal-content-wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Cerca Attività</h2>
                <button
                  className="close-btn"
                  onClick={() => setSearchOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="pianificazione-form">
                <div className="form-row">
                  <label htmlFor="attivita-search-input">
                    Ricerca attività
                  </label>
                  <input
                    id="attivita-search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca per nome cantiere, note o task"
                    autoFocus
                  />
                </div>

                {searchLoading && (
                  <div className="result">
                    Caricamento attività pianificate...
                  </div>
                )}
                {searchError && (
                  <div className="result error">{searchError}</div>
                )}

                {!searchLoading &&
                  !searchError &&
                  normalizedSearchQuery.length > 0 &&
                  normalizedSearchQuery.length < MIN_ACTIVITY_SEARCH_LENGTH && (
                    <div className="result">
                      Inserisci almeno {MIN_ACTIVITY_SEARCH_LENGTH} caratteri
                      per avviare la ricerca.
                    </div>
                  )}

                {!searchLoading &&
                  !searchError &&
                  shouldRenderSearchResults &&
                  filteredSearchActivities.length === 0 && (
                    <div className="result">
                      Nessuna attività trovata per la ricerca inserita.
                    </div>
                  )}

                {!searchLoading &&
                  !searchError &&
                  shouldRenderSearchResults &&
                  filteredSearchActivities.length > 0 && (
                    <div className="search-results-list">
                      {filteredSearchActivities.map((activity) => {
                        const employeeName =
                          userNamesById.get(activity?.userId) ||
                          activity?.userId ||
                          "Dipendente non disponibile";
                        const dateLabel = activity?.date?.toDate
                          ? format(activity.date.toDate(), "dd/MM/yyyy")
                          : "Data non disponibile";
                        const projectName =
                          activity?.project?.nome ||
                          activity?.project?.codice ||
                          "Cantiere non disponibile";
                        const noteLabel =
                          String(activity?.note || "").trim() || "Nessuna nota";
                        const taskLabel = formatTasksInline(activity?.tasks);

                        return (
                          <div key={activity.id} className="search-result-card">
                            <div className="search-result-title">
                              {projectName}
                            </div>
                            <div className="search-result-meta">
                              <span>{employeeName}</span>
                              <span>{dateLabel}</span>
                            </div>
                            <div className="search-result-note">
                              {noteLabel}
                            </div>
                            {taskLabel && (
                              <div className="search-result-tasks">
                                <strong>Task:</strong> {taskLabel}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
    </ResTecnicoAccess>
  );
}
